/**
 * enterprisePanel.js — Полная Enterprise панель для ВСЕХ фич
 * Mikhmel Cornerstone4D DICOM Viewer Pro v6
 *
 * Покрывает ВСЕ 13 viewport-модулей + 5 enterprise-модулей (v6)
 * + 2 storage-модуля (v8):
 *   viewportCamera, viewportCine, viewportScreenshot, viewportAnalysis,
 *   viewportSync, viewport4D, viewportEffects, viewportUI,
 *   viewportKeyboard, viewportPersistence, viewportVoice, viewportState, viewport
 *
 * Enterprise (v6) — 59 новых функций:
 *   sidebarResize (11 fn), viewportResize (8 fn), studyToolsUI (14 fn),
 *   toolBindings (16 fn), uiManager (10 fn)
 *
 * Storage (v8) — 75+ новых функций:
 *   dicomStore (55+ fn), studyQualityAnalyzer (15+ static methods)
 *
 * Compute + Tools (v8) — 35+ новых функций:
 *   gpuCompute (13 fn), vtkModule (12 fn), toolManager (33 fn)
 *
 * IO (v8) — 20+ функций:
 *   dicomSRModule (4 fn), niftiModule (7 fn), dicomWebClient (8 fn), studyArchiveModule (9 fn)
 *
 * Rendering (v8) — cinematic pipeline:
 *   cinematicRendering, cinematicAutoPreset, cinematicMorph, depthExtractor
 *
 * Подключение: import { разложитьРазделы } from "./ui/enterprisePanel.js";
 *
 * ─────────────────────────────────────────────────────────────────
 * [10.3.5] ЧИСТКА ОТ ДУБЛЕЙ. Было 36 разделов и 368 кнопок, из них
 * примерно половина повторяла верхнее меню и сайдбары: список 3D-пресетов
 * существовал в трёх местах сразу, клинические расчёты и ИИ — в двух,
 * окна яркости — в двух.
 *
 * Правило, по которому кнопку убирали: ТО ЖЕ действие с ТЕМИ ЖЕ параметрами
 * доступно в другом месте интерфейса. Если версия панели умеет что-то, чего
 * та кнопка не умеет (другие параметры, другой модуль, связка из двух
 * действий) — кнопка осталась.
 *
 * Проверено поимённо, что «двойник» действительно работает — и искать его
 * пришлось в двух местах: часть кнопок верхнего меню привязана не в src/, а
 * инлайновым модулем в самом index.html. Пока я смотрел только src/, три
 * живые кнопки (btn-gestures-all, btn-dicom-sr, btn-export-gif) выглядели
 * мёртвыми, и я чуть не оставил их дубли в панели.
 *
 * Три места оказались пустыми по-настоящему, и ради них кнопки в панели
 * СОХРАНЕНЫ:
 *   • переключатель «Авто-наложение» в сайдбаре — у него нет даже id, зацепить
 *     нечем → «👁 AI Оверлеи» остаётся;
 *   • btn-export-nifti — была заглушка на один тост → «📤 Export NIfTI»
 *     остаётся, а сама заглушка из сайдбара удалена;
 *   • btn-medsam — умеет только включить → «🎯 SAM выкл» остаётся.
 * ─────────────────────────────────────────────────────────────────
 */

// ── Imports from all modules ──
import {
  set3DPreset,
  applyWindowToSlices,
  setWindowLevel,
  copyScreenshotToClipboard,
  createComparisonStrip,
  batchExportSlices,
  jumpToSystole,
  jumpToDiastole,
  preloadAllPhases,
} from '../core/viewport.js';
import { getRenderingEngine, getViewportIds } from '../core/viewportState.js';

// [10.19.43] таймер-секция удалена (п.7)
import { startCine, stopCine, pauseCine, resumeCine, getCineState, setCineSpeed, getCineStats } from '../core/viewportCine.js';

/** [10.19.43] Где кино живёт: играет (или, при поПаузе, стоит на паузе). */
function _cineГде(толькоИграет = false, толькоПауза = false) {
  for (const id of getViewportIds()) {
    const st = getCineState(id);
    if (!st) continue;
    if (толькоПауза) { if (!st.playing) return id; continue; }
    if (st.playing) return id;
    if (!толькоИграет) return id;
  }
  return null;
}
function _cineСдвигFPS(д) {
  const id = _cineГде();
  if (!id) { showToast('Сначала запустите кино', 'info', 1800); return; }
  const st = getCineState(id);
  setCineSpeed(id, (st.fps || 12) + д);
  showToast(`Скорость: ${getCineState(id).fps} к/с`, 'info', 900);
}
// [10.3.5] импорт из '../core/viewportVoice.js' больше не нужен — все его кнопки убраны как дубли
// [10.19.43] HU-проба переехала в тулбар — импорт ушёл в toolBindings
// [10.19.43] камера А/Б переехала в сайдбар; undo/prompt-пресеты похоронены
import { getTissueTable } from '../core/viewportAnalysisCore.js';
import { getPhaseTimeline } from '../core/viewport4D.js';
// [10.3.5] импорт из '../core/viewportSync.js' больше не нужен — все его кнопки убраны как дубли
// [10.19.43] PiP/закладки-читалка удалены (п.6)
import { setSlabProjection, adjustSlabThickness, applySlabPreset, applyClipPreset } from '../core/viewportEffects.js';;
import { applyDualEnergyMode } from '../core/viewportEffects.js';

// [10.3.5] импорт из '../core/viewportAnalysis.js' больше не нужен — все его кнопки убраны как дубли
import { showToast, спроситьТекст } from '../utils/helpers.js'; // [11.1.18] prompt() в Electron запрещён — своё окно
import { КЛЮЧ_ПЛАШКИ_ЗАМЕРА } from '../core/loadReport.js'; // [11.1.35] выключатель плашек замера
import { log } from '../utils/logger.js';
// [10.19.43] сценарии переехали в сайдбар (п.8)
import { isInitialized as csIsInitialized } from '../core/init.js';
import {
  stopVideoRecording,
  isRecording,
  startVideoRecording,
  exportPDF,
  exportMeasurementsReport,
  exportDICOMReport,
  exportSeriesAsZip,
  downloadScreenshot as exportDownloadScreenshot,
} from './exportModule.js';
// [10.3.5] импорт из '../core/viewportScreenshot.js' больше не нужен — все его кнопки убраны как дубли
import { getVolumeData, runCardiacSegmentation, runBatchAnalysis } from './clinicalTools.js';
import {
  getConnectionHistory,
  clearConnectionHistory,
  testDICOMwebConnection,
  quickConnect as dwebQuickConnect,
  checkServerHealth as dwebCheckHealth,
  exportStudiesJSON as dwebExportStudies,
} from './dicomWebUI.js';
import { getClinicalHistory, clearClinicalHistory, exportClinicalHistoryCSV, listAvailableClinicalTools, isSAMActive, deactivateMedSAM } from './clinicalTools.js';
import {
  StudyQualityAnalyzer,
  getStudies,
  searchStudies,
  getFavourites,
  getProtectedStudies,
  getStorageEstimate,
  verifyAllStudies,
  exportAllStudies,
  getStudyQuality,
  batchAnalyzeQuality,
  compareStudyQuality,
  importStudy as dsImportStudy,
} from '../storage/dicomStore.js';
// [10.3.5] импорт из '../core/streamingManager.js' больше не нужен — все его кнопки убраны как дубли
import { computeVolumeByThreshold, isVtkAvailable, pixelsToSTL } from '../compute/vtkModule.js';
import { applyToolPreset } from '../tools/toolManager.js';
// [10.3.5] импорт из '../io/dicomSRModule.js' больше не нужен — все его кнопки убраны как дубли
import { initNiftiLoader, loadNiftiFile, exportToNifti } from '../io/niftiModule.js';
import { createClient as dwCreateClient, getClient as dwGetClient } from '../io/dicomWebClient.js';
// [10.3.5] импорт из '../io/studyArchiveModule.js' больше не нужен — все его кнопки убраны как дубли
import { rotateViewport, getToolHistory, toggleToolFavorite, undoAnnotation as tbUndo, redoAnnotation as tbRedo } from './toolBindings.js';
import { getSeriesListState, filterSeriesByModality, searchSeries, navigateSeriesDirection, sortSeries, showOnlyFavorites } from './seriesPanel.js';
import { applyProfileToUI, getProfileStats, createCustomProfile, setAutoSwitchByModality, setProfile, resetProfile } from './profileManager.js';
import { getAIHistory, getActiveVolumeInfo, getActiveDatasetId } from './proFeaturesBindings.js'; // [10.19.43] порог/прогрев/оверлеи переехали во вкладку ИИ
import { getAllShortcuts, setHotkeysEnabled, addBinding, exportBindingsJSON, importBindingsFromJSON, toggleCheatSheet, detectConflicts } from './hotkeys.js';
import { searchHelp, showToolHelp, showModeHelp, startWalkthrough, nextWalkthroughStep, stopWalkthrough } from './inlineHelp.js';
import { mobileMPRFocus, restoreMaximized, saveLayoutPreset, listLayoutPresets, cycleLayout, enterComparisonMode, loadLayoutPreset } from './layoutManager.js';
import { getWLHistory, restoreWLFromHistory, getUserVRTPresetsAPI, syncCinematicSlidersAPI } from './sidebarBindings.js';
import { toggleCinematicPanelExport } from './studyToolsUI.js';
import { setTheme, saveWorkspace, listWorkspaces, startAutoBackup, stopAutoBackup, restoreWorkspace } from './uiManager.js';
import { getExportHistory, clearExportHistory } from './exportModule.js';
import { getLoadHistory, cancelLoad as fopsCancelLoad } from './fileOps.js';
import { toggleFPSOverlay, detectThermalThrottling, runGPUBenchmark } from './gpuUI.js';

// ── [7.0.38] Иконки: emoji → SVG-спрайт. Логика в отдельном модуле enterpriseIcons.js
// (её же можно подключить в других UI-файлах). Ниже — только вызовы в рендере. ──
import { escHtml } from './enterpriseIcons.js'; // [10.19.46] iconize* больше не нужны: ярлыки без значков
import { setNotifyLevel as setRecoveryNotify } from './recoveryReport.js';   // [9.0.4] выключатель плашек

// ── featureBindings lazy AI ──
let _lazyAI = null;
const _ai = async () => _lazyAI || (_lazyAI = await import('../ai/aiModule.js'));

// ══════════════════════════════════════════════════════════════
//  STATE
// ══════════════════════════════════════════════════════════════

// [10.19.47] _panelEl и _expanded удалены вместе с плавающей панелью.
// [11.0.2] _epRefreshInterval удалена вместе с вечным опросом (см. ниже):
// хранить идентификатор таймера, которого нет, незачем.
let _ac = new AbortController(); // [FIX] AbortController для очистки addEventListener

const _toggles = {
  huProbe: false,
  mprSync: false,
  posSync: false,
  adaptive: false,
  infoOverlay: false,
  crosshair: false,
  magnifier: false,
  grid: false,
  studyTimer: false,
  recording: false,
  gestures: false,
};

const _vpId = () => getViewportIds()[0] || null;
// For CINE: pick a scrollable viewport (stack or orthographic), not 3D
const _cineVpId = () => {
  const re = getRenderingEngine();
  if (!re) return _vpId();
  for (const vp of re.getViewports()) {
    if (vp.type === 'stack' || vp.type === 'orthographic') return vp.id;
  }
  return _vpId();
};
const _getVolumeId = () => {
  try {
    const re = getRenderingEngine();
    if (!re) return null;
    for (const vp of re.getViewports()) {
      const actors = vp.getActors?.();
      if (actors?.length) {
        const a = actors[0];
        const id = a.uid || a.referenceId || a.referencedId || a.volumeId || a.id;
        if (id) return id;
      }
    }
  } catch (e) { /* safe */ }
  return null;
};

function _find3DVpId() {
  const re = getRenderingEngine();
  if (!re) return _vpId();
  for (const vp of re.getViewports()) {
    if (vp.type === 2 || (vp.id && (vp.id.includes('3d') || vp.id.includes('volume')))) return vp.id;
  }
  return _vpId();
}

// ══════════════════════════════════════════════════════════════
//  ALL SECTIONS (10 sections, ~80 buttons)
// ══════════════════════════════════════════════════════════════

const SECTIONS = [
  // ══════════════════════════════════════════════════════════════
  //  1. CINE (viewportCine.js)
  // ══════════════════════════════════════════════════════════════
  {
    id: 'diag', icon: '', label: 'Диагностика',
    buttons: [
      // [10.5.1] Опыт на телефоне: сохранение исследования в хранилище браузера
      // читает каждый файл ещё раз и пишет гигабайты — одновременно со сборкой
      // тома. Выключатель нужен, чтобы измерить его цену, а не рассуждать о ней.
      { label: 'Сохранение в хранилище: ВЫКЛ (для опыта)', action: () => { try { localStorage.setItem('mikhmel_bg_save','off'); showToast('Фоновое сохранение выключено. Загрузите каталог заново и посмотрите строку [Время] в журнале. Объём теперь НЕ переживёт выгрузку вкладки.', 'warning', 9000); } catch(e){} } },
      { label: 'Сохранение в хранилище: включить обратно', action: () => { try { localStorage.removeItem('mikhmel_bg_save'); showToast('Фоновое сохранение включено — как обычно.', 'success', 4000); } catch(e){} } },
      { label: 'Отчёт о восстановлении тома', action: () => { try { globalThis.MikhmelViewer?.recoveryReport?.(); } catch (e) { /* */ } } },
      // [10.19.5] Замер последней загрузки. Плашка больше не выскакивает после
      // каждой нормальной загрузки (только когда файлы не сошлись со срезами
      // или слишком долго на файл) — а посмотреть её можно здесь, когда нужно.
      // [10.19.6] Какая сборка запущена — одной кнопкой. Нужно оказалось не для
      // красоты: правки были сделаны, а на экране оставалась прежняя программа
      // (запускался старый собранный dist), и понять это по виду было нельзя.
      { label: 'Какая версия запущена', долгоеНажатие: true, action: () => { try {
        const v = (typeof __APP_VERSION__ !== 'undefined') ? __APP_VERSION__ : 'неизвестна';
        const св = (globalThis.__SW_CACHE__ || '');
        showToast(`Версия программы: ${v}${св ? ` · кеш ${св}` : ''}. Если число не то, которое вы ставили — `
          + 'запущена прежняя сборка: замените папку dist и обновите страницу (Ctrl+F5).', 'info', 12000);
      } catch (e) { /* */ } } },
      { label: 'Замер последней загрузки', action: async () => { try { const m = await import('./fileOps.js'); m.показатьЗамерЗагрузки(); } catch (e) { showToast('Замер недоступен: ' + e.message, 'warning', 4000); } } },
      // [11.1.35] Выключатель плашек замера — просьба владельца: «панель была
      // нужна для диагностики, а сейчас я её хочу скрыть. Скрыть, а не
      // удалить». Гасит обе плашки о скорости (обход каталога и время
      // загрузки) и их WARN-спам в журнале; кнопка «Замер последней
      // загрузки» выше продолжает показывать замер по требованию. Правило —
      // в core/loadReport.js (там же честная оговорка: потеря срезов
      // показывается всегда — это не скорость, а неполные данные).
      { label: 'Замер после загрузки: скрыть',
        active: () => { try { return localStorage.getItem(КЛЮЧ_ПЛАШКИ_ЗАМЕРА) === 'off'; } catch (e) { return false; } },
        action: () => { try { localStorage.setItem(КЛЮЧ_ПЛАШКИ_ЗАМЕРА, 'off'); showToast('Плашки замера скрыты. Замер по-прежнему считается: журнал и кнопка «Замер последней загрузки». Предупреждение о ПОТЕРЕ срезов показывается всегда.', 'info', 7000); } catch (e) { showToast('Не удалось сохранить настройку: ' + e.message, 'error', 3000); } } },
      { label: 'Замер после загрузки: показывать',
        active: () => { try { return localStorage.getItem(КЛЮЧ_ПЛАШКИ_ЗАМЕРА) !== 'off'; } catch (e) { return true; } },
        action: () => { try { localStorage.removeItem(КЛЮЧ_ПЛАШКИ_ЗАМЕРА); showToast('Плашки замера снова показываются, когда замер о беде (не сошлось или долго на файл).', 'info', 4500); } catch (e) { showToast('Не удалось сохранить настройку: ' + e.message, 'error', 3000); } } },
      // [10.19.9] Модели ИИ в проект не входят (около 400 МБ) — их кладёт врач
      // руками. До сих пор проверить это можно было только нажатием ИИ-кнопки:
      // молчаливый переход на алгоритмический откат от работы модели на экране
      // не отличить. Теперь спрашивается прямо.
      // [10.19.29] «Модели ИИ: что найдено» УБРАНА отсюда по решению владельца:
      // проверка переехала в саму вкладку ИИ (кнопка «Проверить модели»), где
      // моделями и пользуются. Второй вход в служебной панели снова разводил бы
      // одну функцию по двум местам.
      { label: 'Плашки: не показывать', action: () => { setRecoveryNotify('off'); showToast('Плашки восстановления выключены. Журнал ведётся, отчёт — кнопкой выше.', 'info', 4000); } },
      { label: 'Плашки: только важное', action: () => { setRecoveryNotify('important'); showToast('Плашка появится только когда том не удалось вернуть.', 'info', 4000); } },
      { label: 'Плашки: показывать все', action: () => { setRecoveryNotify('all'); showToast('Плашки восстановления включены полностью.', 'info', 3000); } },
    ],
  },
  {
    id: 'cine', icon: '', label: 'CINE Прокрутка',
    buttons: [
      // [10.3.5] Четыре кнопки ниже не работали с самого начала: getCineState,
      // pauseCine, setCineSpeed и getCineStats принимают ПЕРВЫМ аргументом
      // идентификатор окна, а звали их без него. setCineSpeed(15) уходило
      // искать окно с именем «15», не находило и молча выходило. Теперь окно
      // передаётся — то же самое, что и у Play.
      // [10.19.43] Прицел — окно, ГДЕ КИНО ЖИВЁТ (играет или на паузе), а не
      // «первое прокручиваемое»: Пауза и ±5 били мимо, если кино шло не в
      // первом окне (п.2 владельца). Пара к Паузе — «Продолжить» (ядро
      // resumeCine было, кнопки не было). ±5 теперь без рестарта и глушат
      // адаптив (правка ядра) — конец «работает так, как хочет».
      { label: 'Вперёд', action: () => startCine(_cineГде() || _cineVpId(), { fps: 15, loop: true, adaptiveFPS: true }), active: () => !!_cineГде(true) },
      { label: 'Пауза', action: () => { const id = _cineГде(true); if (!id) { showToast('Кино не запущено', 'info', 1500); return; } pauseCine(id); } },
      { label: 'Продолжить', action: () => { const id = _cineГде(false, true); if (!id) { showToast('Нет кино на паузе', 'info', 1500); return; } resumeCine(id); } },
      { label: 'Стоп', action: () => stopCine() },
      { label: '−5 к/с', action: () => _cineСдвигFPS(-5) },
      { label: '+5 к/с', action: () => _cineСдвигFPS(+5) },
      { label: 'Туда-обратно', action: () => startCine(_cineГде() || _cineVpId(), { fps: 12, direction: 'bounce', loop: true }) },
      { label: 'Назад', action: () => startCine(_cineГде() || _cineVpId(), { fps: 12, direction: 'backward', loop: true }) },
      { label: 'Статистика', action: () => { const s = getCineStats(_cineVpId()); if (s) showToast(`FPS: ${s.avgFPS.toFixed(1)}, Потери: ${(s.dropRate * 100).toFixed(0)}%`, 'info', 3000); else showToast('Прокрутка ещё не запускалась', 'info', 2000); } },
    ],
  },

  // ══════════════════════════════════════════════════════════════
  //  2. ЭКСПОРТ (объединено: screenshot + export + exportModule + DICOM SR)
  // ══════════════════════════════════════════════════════════════
  {
    id: 'export', icon: '', label: 'Экспорт (Фото / Видео / PDF / Отчёт)',
    buttons: [
      // ── Скриншоты ──
      // [10.3.5] Убраны: «Скриншот PNG» (меню Экспорт → Снимок экрана,
      // btn-screenshot), «Анонимный снимок» (btn-screenshot-anon),
      // «Все viewport» и «Пакет все VP» (правый сайдбар → Спец. режимы →
      // Расширенный экспорт, btn-pro-screenshot-all).
      // [10.19.27] «Скриншот JPEG» убран — меню Экспорт → «Снимок экрана»
      // (btn-screenshot, Ctrl+E) делает ту же операцию: снимок вьюпорта в файл.
      // Формат — параметр, а не отдельная функция; второй вход был дублем.
      { label: 'В буфер', action: () => copyScreenshotToClipboard(_vpId()) },
      { label: 'С водяным знаком', action: () => { try { exportDownloadScreenshot({ watermark: 'КОНФИДЕНЦИАЛЬНО', includeOverlays: true }); } catch(e) { showToast('❌ ' + e.message, 'error', 2000); } } },
      { label: 'Лента 6 срезов', action: async () => { const url = await createComparisonStrip(_vpId(), 6); if (url) _downloadUrl(url, 'strip.png'); } },
      { label: 'Лента 12 срезов', action: async () => { const url = await createComparisonStrip(_vpId(), 12); if (url) _downloadUrl(url, 'strip12.png'); } },
      { label: 'Пакет (каждый 5-й)', action: async () => { showToast('⏳ Экспорт...', 'info', 2000); const r = await batchExportSlices(_vpId(), { step: 5, onProgress: (c, t) => showToast(`${c}/${t}`, 'info', 500) }); showToast(`✅ ${r.length} файлов`, 'success', 2000); } },
      // ── Видео ──
      { label: 'Видео: прокрутка', action: () => { try { startVideoRecording({ mode: 'scroll', fps: 15 }); showToast('🎥 Запись...', 'info', 1500); } catch(e) { showToast('❌ ' + e.message, 'error', 2000); } } },
      { label: 'Стоп запись', action: () => { try { stopVideoRecording(); showToast('⏹ Запись остановлена', 'info'); } catch(e) { showToast('❌ ' + e.message, 'error', 2000); } }, active: () => isRecording() },
      // [10.3.5] «GIF анимация» убрана — меню Экспорт → Cine (btn-cine-gif).
      // [10.19.27] «HR скриншот» убран — третий вход в тот же снимок вьюпорта
      // (btn-screenshot в меню Экспорт). Отличие было только в том, что канвас
      // сохранялся как есть, без оверлеев, — это настройка, не функция.
      // [10.19.27] Пара «Запись 60FPS / Стоп 60FPS» убрана — верхняя панель,
      // «Запись экрана» (btn-screen-record) пишет тот же холст тем же
      // MediaRecorder; частота кадров и битрейт — параметры, не функция.
      // Запись с автопрокруткой («Видео: прокрутка») оставлена: такой в
      // главном интерфейсе нет.
      // ── PDF / Отчёты ──
      { label: 'PDF (текущий)', action: () => { try { exportPDF({ allViewports: false }); } catch(e) { showToast('❌ ' + e.message, 'error', 2000); } } },
      { label: 'PDF (все окна)', action: () => { try { exportPDF({ allViewports: true }); } catch(e) { showToast('❌ ' + e.message, 'error', 2000); } } },
      { label: 'PDF + заключение', action: async () => { const notes = await спроситьТекст('Заключение врача:', { многострочно: true }); if (notes) { try { exportPDF({ allViewports: true, notes, title: 'Заключение DICOM' }); } catch(e) { showToast('❌ ' + e.message, 'error', 2000); } } } },
      // [10.3.5] «Радиол. отчёт» убран — правый сайдбар, вкладка «Отчёты»:
      // там те же три поля (находки, заключение, врач) нормальной формой,
      // а не тремя окошками prompt подряд.
      { label: 'Отчёт по измерениям', action: async () => { const p = await спроситьТекст('Врач (необязательно):'); try { exportMeasurementsReport({ physician: p }); } catch(e) { showToast('❌ ' + e.message, 'error', 2000); } } },
      { label: 'DICOM отчёт', action: () => { try { exportDICOMReport(); } catch(e) { showToast('❌ ' + e.message, 'error', 2000); } } },
      // [10.3.5] Убраны: «DICOM SC» (правый сайдбар → Спец. режимы →
      // Расширенный экспорт, btn-pro-dicom-capture) и «DICOM SR»
      // (меню Инструменты → Отчёты, btn-dicom-sr — обработчик стоит
      // инлайном в index.html; копия из правого сайдбара убрана в 10.3.7).
      { label: 'ZIP серия', action: () => exportSeriesAsZip({ format: 'jpeg' }) },
      // ── История ──
      { label: 'История экспорта', action: () => { const h = getExportHistory(); showToast(h.length ? h.map(e => `${e.когда} ${e.format}${e.filename ? ' — ' + e.filename : ''}`).join('\\n') : 'Пусто', 'info', 6000); } },
      { label: 'Очистить историю', action: () => clearExportHistory() },
    ],
  },

  // ══════════════════════════════════════════════════════════════
  //  3. АНАЛИЗ / ИЗМЕРЕНИЯ (viewportAnalysis.js)
  // ══════════════════════════════════════════════════════════════
  // [10.19.43] Секция «Анализ / Измерения» РАСПУЩЕНА по решению владельца
  // (п.4, дословно: «эту функцию нужно тщательно проверить… внести в
  // основной инструментарий, удалив из энтерпрайз панели»). HU-проба
  // переехала в ТУЛБАР (btn-tool-hu-probe, toolBindings) — с гейтом
  // модальности: на МР/УЗИ показывается «интенсивность», не «HU».

  // ══════════════════════════════════════════════════════════════
  //  4. 3D КАМЕРА (viewportCamera.js)
  // ══════════════════════════════════════════════════════════════
  // [10.19.43] Секция «3D Камера» РАСПУЩЕНА (п.5 владельца: «сравнение
  // ракурсов — важная функция, нужно её сделать достойной»). Достойное
  // сравнение А↔Б переехало в блок «3D VRT & ВИД» левого сайдбара: ряд
  // «Ракурсы» с подсветкой сохранённых видов (getCameraABState) и
  // анимацией 400 мс — без единого prompt(). Undo/Redo камеры и
  // prompt-пресеты похоронены; ядро viewportCamera живо для будущего.

  // ══════════════════════════════════════════════════════════════
  //  5. 3D РЕНДЕРИНГ (viewport.js core + cinematic)
  // ══════════════════════════════════════════════════════════════

  // ══════════════════════════════════════════════════════════════
  //  6. ПРОЕКЦИИ / КЛИППИНГ / DE (viewportEffects.js)
  // ══════════════════════════════════════════════════════════════
  {
    id: 'effects', icon: '', label: 'Проекции / Клиппинг / DE',
    buttons: [
      // [10.3.5] Убраны: «Цикл MIP» (правый сайдбар → Спец. режимы → Slab
      // Projection, btn-pro-slab-cycle) и «Slab ±5mm» (там же ползунок
      // толщины slab-thickness; к тому же обе эти кнопки не работали —
      // adjustSlabThickness ждёт первым аргументом окно, а получала шаг).
      //
      // Клинические пресеты слэба и клиппинга оставлены: в сайдбаре есть
      // только ручные режим/толщина и ползунки X/Y/Z, а именованных
      // протокольных пресетов там нет.
      { label: 'ТЭЛА MIP 10mm', action: () => applySlabPreset(_vpId(), 'pe-protocol') },
      { label: 'Бронхи MinIP 5mm', action: () => applySlabPreset(_vpId(), 'airway') },
      { label: 'Сосуды MIP 20mm', action: () => applySlabPreset(_vpId(), 'vessel-mip') },
      { label: 'Интерстиций AvgIP', action: () => applySlabPreset(_vpId(), 'interstitium') },
      { label: 'Слэб ВЫКЛ', action: () => setSlabProjection(_vpId(), 'off') },
      // [10.3.5] Четыре кнопки клиппинга не работали: applyClipPreset ждёт
      // (окно, пресет), а получала только пресет — и CLIP_PRESETS[undefined]
      // молча ничего не давал. Окно передано.
      { label: 'Клип: кардио', action: () => applyClipPreset(_find3DVpId(), 'cardiac-anterior') },
      { label: 'Клип: позвоночник', action: () => applyClipPreset(_find3DVpId(), 'spine-anterior') },
      { label: 'Клип: живот верх', action: () => applyClipPreset(_find3DVpId(), 'abdomen-upper') },
      { label: 'Клип: сброс', action: () => applyClipPreset(_find3DVpId(), 'reset') },
      { label: 'DE: Нативный', action: () => applyDualEnergyMode(_vpId(), 'virtual-non-contrast') },
      { label: 'DE: Карта йода', action: () => applyDualEnergyMode(_vpId(), 'iodine-map') },
      { label: 'DE: Без кальция', action: () => applyDualEnergyMode(_vpId(), 'calcium-suppress') },
      { label: 'DE: Без костей', action: () => applyDualEnergyMode(_vpId(), 'bone-remove') },
      { label: 'DE: Перфузия', action: () => applyDualEnergyMode(_vpId(), 'lung-perfusion') },
    ],
  },

  // ══════════════════════════════════════════════════════════════
  //  7. W/L ПРЕСЕТЫ (viewportKeyboard.js)
  // ══════════════════════════════════════════════════════════════

  // ══════════════════════════════════════════════════════════════
  //  8. ОВЕРЛЕИ / ЗАКЛАДКИ (viewportUI.js)
  // ══════════════════════════════════════════════════════════════
  // [10.19.43] Секция «Оверлеи / Закладки» УДАЛЕНА (п.6 владельца:
  // «удалить раздел»). PiP рисовал картинку-в-картинке БЕЗ маркера
  // положения — навигатором не был; кандидат «настоящий навигатор с
  // рамкой позиции» записан. Список закладок тостом — читалка живых
  // закладок сайдбара, самостоятельной ценности не имел.

  // ══════════════════════════════════════════════════════════════
  //  9. СИНХРОНИЗАЦИЯ / СРАВНЕНИЕ (viewportSync.js)
  // ══════════════════════════════════════════════════════════════

  // ══════════════════════════════════════════════════════════════
  //  10. 4D CARDIAC (viewport4D.js)
  // ══════════════════════════════════════════════════════════════
  {
    id: '4d', icon: '', label: '4D Кардио',
    buttons: [
      // [10.3.5] «Play 4D» и «Stop 4D» убраны — правый сайдбар → Совмещение
      // и 4D → 4D Кардио-фазы: btn-pro-4d-play / btn-pro-4d-stop.
      { label: 'Систола', action: () => { try { jumpToSystole(); showToast('💓 Систола', 'info', 1000); } catch(e) { showToast('❌ ' + e.message, 'error', 2000); } } },
      { label: 'Диастола', action: () => { try { jumpToDiastole(); showToast('💓 Диастола', 'info', 1000); } catch(e) { showToast('❌ ' + e.message, 'error', 2000); } } },
      // [10.3.5] Шаг по фазам вперёд/назад убран — там же,
      // btn-pro-4d-next / btn-pro-4d-prev.
      { label: 'Прогрев фаз', action: () => { try { preloadAllPhases((c, t) => showToast(`${c}/${t}`, 'info', 500)); } catch(e) { showToast('❌ ' + e.message, 'error', 2000); } } },
      { label: 'Фазы списком', action: () => { try { const tl = getPhaseTimeline(); if (tl.length) showToast(tl.map(p => `${p.isCurrent ? '►' : ' '} ${p.label}${p.isSystole ? ' [S]' : ''}${p.isDiastole ? ' [D]' : ''}`).join('\n'), 'info', 8000); else showToast('4D не инициализирован — нужны многофазные данные', 'info', 2000); } catch(e) { showToast('❌ ' + e.message, 'error', 2000); } } },
    ],
  },

  // ══════════════════════════════════════════════════════════════
  //  11. СЕССИЯ / ЗАПИСЬ / АННОТАЦИИ (viewportPersistence.js + viewportVoice.js + viewportKeyboard.js)
  // ══════════════════════════════════════════════════════════════
  // [10.19.43] Секция «Сессия / Запись / Аннотации» УДАЛЕНА (п.7
  // владельца: «удалить раздел»). Таймер был шахматными часами без
  // документа — elapsed никуда не шёл; ядро persistence живо, и если
  // понадобится «Время анализа: N мин» строкой в PDF-отчёте — проведём
  // от ядра, не от кнопки.

  // ══════════════════════════════════════════════════════════════
  //  12. КЛИНИЧЕСКИЕ WORKFLOWS (presetsManager.js)
  // ══════════════════════════════════════════════════════════════
  // [10.19.43] Секция «Клинические Workflows» ПЕРЕЕХАЛА (п.8 владельца:
  // «переселить рядом с пресетами в правом сайдбаре отдельным рядом
  // „Сценарии“»). Теперь: правый сайдбар → ряд «Сценарии», шесть кнопок
  // data-workflow, ВСЁ через реестр CLINICAL_WORKFLOWS (панель половину
  // сценариев зашивала мимо реестра — вылечено наполнением реестра).

  // ══════════════════════════════════════════════════════════════
  //  13. КЛИНИЧЕСКИЕ ИНСТРУМЕНТЫ (clinicalTools.js — единая секция)
  // ══════════════════════════════════════════════════════════════
  {
    id: 'clinical', icon: '', label: 'Клинические инструменты',
    buttons: [
      // [10.3.5] Восемь расчётов убраны — все они есть в правом сайдбаре,
      // вкладка «Клинические», и там зовут ровно те же функции из
      // clinicalTools.js: Индекс Агатстона (btn-agatston), Эмфизема
      // (btn-emphysema), CTR (btn-ctr), BMD (btn-bmd), Пневмоторакс
      // (btn-pneumothorax), Переломы (btn-fracture-detect), Диаметр аорты
      // (btn-aorta-diameter), Рост области 3D (btn-region-grow).
      //
      // «Камеры сердца + EF» оставлены: в сайдбаре такой кнопки нет.
      { label: 'Камеры сердца + EF', action: () => { const vid = _getVolumeId(); if (!vid) { showToast('❌ Нет 3D объём', 'error', 2000); return; } runCardiacSegmentation(vid); } },
      // ── SAM ──
      // [10.3.5] Убраны: «SAM вкл» (btn-medsam), «SAM undo» (btn-sam-undo),
      // «SAM очистить» (btn-sam-clear), «SAM запуск» (btn-sam-run).
      // «SAM выкл» ОСТАВЛЕНА: btn-medsam умеет только включить —
      // deactivateMedSAM в сайдбаре не вызывается ниоткуда.
      { label: 'SAM выкл', action: () => deactivateMedSAM(), active: () => isSAMActive() },
      // ── Батч-анализ ──
      { label: 'Пакет: Кардио', action: async () => { showToast('⏳ Пакет...', 'info', 2000); const r = await runBatchAnalysis(_getVolumeId(), ['agatston','ctr','cardiac']); const msg = Object.entries(r).map(([k,v]) => `${k}: ${v.error || '✅'}`).join('\n'); showToast(msg, 'info', 6000); } },
      { label: 'Пакет: Лёгкие', action: async () => { showToast('⏳ Пакет...', 'info', 2000); const r = await runBatchAnalysis(_getVolumeId(), ['emphysema','pneumothorax']); const msg = Object.entries(r).map(([k,v]) => `${k}: ${v.error || '✅'}`).join('\n'); showToast(msg, 'info', 6000); } },
      { label: 'Пакет: ВСЁ', action: async () => { showToast('⏳ Полный анализ...', 'info', 3000); const tools = listAvailableClinicalTools().filter(t => t !== 'medSAM' && t !== 'regionGrow'); const r = await runBatchAnalysis(_getVolumeId(), tools); const msg = Object.entries(r).map(([k,v]) => `${k}: ${v.error || '✅'}`).join('\n'); showToast(msg, 'info', 8000); } },
      // ── История ──
      { label: 'История анализов', action: () => { const h = getClinicalHistory(); showToast(h.length ? h.slice(-8).map(x => `${x.tool} (${new Date(x.timestamp).toLocaleTimeString()})`).join('\n') : 'Пусто', 'info', 5000); } },
      { label: 'Экспорт CSV', action: () => exportClinicalHistoryCSV() },
      { label: 'Очистить историю', action: () => { clearClinicalHistory(); showToast('🗑 Очищено', 'info', 1000); } },
    ],
  },

  // ══════════════════════════════════════════════════════════════
  //  14. ИИ-МОДЕЛИ / СЕГМЕНТАЦИЯ (ai-router + pro-features)
  // ══════════════════════════════════════════════════════════════
  // [10.19.43] Секция «ИИ-модели / Сегментация» ПЕРЕЕХАЛА (п.9 владельца:
  // «переезд»). Вкладка ИИ правого сайдбара получила ряд «Управление»:
  // порог уверенности — ПОЛЗУНКОМ (вместо кнопок-хардкодов 30/50%),
  // «Прогрев моделей», переключатель «Показ масок ИИ».

  // ══════════════════════════════════════════════════════════════
  //  15. DICOMweb / PACS (объединено: dicomweb + dicomweb-pro + dicomweb-client)
  // ══════════════════════════════════════════════════════════════
  // [10.19.46] «DICOMweb / PACS» УДАЛЕНА (решение владельца, п.1: «все эти
  // пункты есть в верхнем меню в пункте „Файл“»). Живой вход: Файл → WADO
  // URL (btn-open-url) и окно WADO-RS; на телефоне — кнопка URL и «Ещё».
  // «Экспорт JSON» списка найденных похоронен по слову владельца: не нужен.

  // ══════════════════════════════════════════════════════════════
  //  16. ГОЛОСОВОЕ УПРАВЛЕНИЕ
  // ══════════════════════════════════════════════════════════════

  // ══════════════════════════════════════════════════════════════
  //  17. ПРОФИЛЬ ВРАЧА (profileManager.js)
  // ══════════════════════════════════════════════════════════════
  // [10.19.46] «Профиль врача / Специализация» УДАЛЕНА (решение владельца,
  // п.2). Живой вход — иконка профиля в верхней панели (btn-profile, ожила
  // в 10.19.31). «Авто по модальности» ПЕРЕСЕЛЕНО в само окно выбора
  // профиля переключателем — по слову владельца. «Создать профиль»
  // похоронен: «не нужны».

  // ══════════════════════════════════════════════════════════════
  //  18. РАСКЛАДКА / ПРЕСЕТЫ (layoutManager.js)
  // ══════════════════════════════════════════════════════════════
  {
    id: 'layout-mgr', icon: '', label: 'Раскладка / Пресеты / Сравнение',
    buttons: [
      // [10.3.5] Четыре раскладки (1×1, 1×2, 2×2, MPR) убраны — меню
      // Вид → Раскладка. Цикл, сравнение, пресеты и фокус остались:
      // в меню их нет.
      { label: 'Цикл раскладки', action: () => cycleLayout() },
      // [10.19.27] «Режим сравнения» убран — с 10.9.0 вход в сравнение один:
      // кнопка «Сравнение» в верхней панели (btn-compare-mode, comparePanel).
      // Здесь был ВТОРОЙ вход, к тому же старым механизмом enterComparisonMode
      // из viewportSync — два разных кода на одну операцию и расползание
      // поведения. Комментарий в index.html:3003 прямо говорит: «вход теперь
      // кнопкой».
      { label: 'Сохранить пресет…', action: async () => { const n = await спроситьТекст('Имя пресета:'); if (n) saveLayoutPreset(n); } },
      { label: 'Загрузить пресет…', action: async () => { const list = listLayoutPresets(); if (!list.length) { showToast('Нет пресетов', 'info', 1200); return; } const n = await спроситьТекст('Загрузить пресет', { подсказка: 'Доступные: ' + list.map(l => l.name).join(', ') }); if (n) loadLayoutPreset(n); } },
      { label: 'Фокус: аксиальный', action: () => mobileMPRFocus('axial') },
      { label: 'Фокус: сагиттальный', action: () => mobileMPRFocus('sagittal') },
      { label: 'Фокус: корональный', action: () => mobileMPRFocus('coronal') },
      { label: 'Восстановить всё', action: () => restoreMaximized() },
    ],
  },

  // ══════════════════════════════════════════════════════════════
  //  19. ИНСТРУМЕНТЫ / ТРАНСФОРМЫ / UNDO (toolBindings.js)
  // ══════════════════════════════════════════════════════════════
  // [10.19.46] «Инструменты / Трансформы / Отмена» УДАЛЕНА (решение
  // владельца, п.4: «не вижу ценности»). Отмена/Повтор живут горячими
  // клавишами Ctrl+Z / Ctrl+Shift+Z (toolBindings), повороты — в меню Вид
  // (±90°; 180° = два нажатия), избранное и история инструментов
  // похоронены как забава.

  // ══════════════════════════════════════════════════════════════
  //  20. СЕРИИ / НАВИГАЦИЯ (seriesPanel.js)
  // ══════════════════════════════════════════════════════════════
  // [10.19.46] «Серии / Навигация / Фильтр» УДАЛЕНА (решение владельца,
  // п.5). Живая панель «Серии» — левый сайдбар (список, разворот,
  // закрепление). Фильтр по модальности, поиск, сортировка и «избранные
  // серии» похоронены; если понадобятся — им место строкой поиска в самом
  // заголовке «Серии», а не отдельной панелью.

  // ══════════════════════════════════════════════════════════════
  //  21. ГОРЯЧИЕ КЛАВИШИ (hotkeys.js)
  // ══════════════════════════════════════════════════════════════
  {
    id: 'hotkeys', icon: '', label: 'Горячие клавиши / Шпаргалка',
    buttons: [
      { label: 'Шпаргалка', action: () => toggleCheatSheet() },
      { label: 'Все шорткаты', action: () => { const all = getAllShortcuts(); showToast(all.slice(0, 15).map(s => `[${s.category}] ${s.key}: ${s.action}`).join('\n'), 'info', 10000); } },
      { label: 'Выключить', action: () => { setHotkeysEnabled(false); showToast('⏸ Горячие клавиши ВЫКЛ', 'warning', 1000); } },
      { label: 'Включить', action: () => { setHotkeysEnabled(true); showToast('▶ Горячие клавиши ВКЛ', 'success', 1000); } },
      { label: 'Добавить шорткат', action: async () => { const code = await спроситьТекст('Код клавиши (напр. KeyG):'); if (!code) return; const act = await спроситьТекст('Действие (напр. toggleGrid):'); if (code && act) { addBinding(code, act, act); showToast(`✅ ${code} → ${act}`, 'success'); } } },
      { label: 'Конфликты', action: () => { const c = detectConflicts(); showToast(c.length ? c.map(x => `${x.code}: ${x.bindings.length} bindings`).join('\n') : '✅ Нет', c.length ? 'warning' : 'success', 3000); } },
      { label: 'Экспорт привязок', action: () => exportBindingsJSON() },
      { label: 'Импорт привязок', action: async () => { const j = await спроситьТекст('Импорт привязок', { многострочно: true, подсказка: 'Вставьте JSON' }); if (j) { const n = importBindingsFromJSON(j); showToast('📥 ' + n, 'success', 1200); } } },
    ],
  },

  // ══════════════════════════════════════════════════════════════
  //  22. СПРАВКА / ОБЗОР UI (inlineHelp.js)
  // ══════════════════════════════════════════════════════════════
  {
    id: 'inline-help', icon: '', label: 'Справка / Обзор интерфейса',
    buttons: [
      { label: 'Начать обзор', action: () => startWalkthrough() },
      { label: 'Дальше', action: () => nextWalkthroughStep() },
      { label: 'Стоп обзор', action: () => stopWalkthrough() },
      { label: 'Поиск по справке…', action: async () => { const q = await спроситьТекст('Поиск в справке:'); if (q) { const r = searchHelp(q); showToast(r.length ? r.map(h => `${h.title}`).join('\\n') : 'Ничего не найдено', 'info', 6000); } } },
      { label: 'Справка: W/L', action: () => showToolHelp('WindowLevel') },
      { label: 'Справка: Длина', action: () => showToolHelp('Length') },
      { label: 'Справка: MPR', action: () => showModeHelp('mpr') },
    ],
  },

  // ══════════════════════════════════════════════════════════════
  //  23. DICOM Store (упрощённый для врача)
  // ══════════════════════════════════════════════════════════════
  // [10.19.46] «DICOM-хранилище / Исследования» УДАЛЕНА (решение владельца,
  // п.6). Живой вход — Файл → Архив пациентов (окно с поиском и очисткой).
  // «Проверка целостности» и «Экспорт все» ПЕРЕСЕЛЕНЫ в это окно — они
  // единственные не имели дома, а выгрузка архива целиком нужна при
  // переносе на другую машину.

  // ══════════════════════════════════════════════════════════════
  //  24. NIfTI (niftiModule.js)
  // ══════════════════════════════════════════════════════════════
  {
    id: 'nifti', icon: '', label: 'NIfTI / Загрузка / Экспорт',
    buttons: [
      { label: 'Запустить загрузчик', action: async () => { try { await initNiftiLoader(); showToast('✅ Загрузчик NIfTI зарегистрирован', 'success', 1500); } catch(e) { showToast('❌ ' + e.message, 'error', 3000); } } },
      { label: 'Загрузить .nii', action: () => { const input = document.createElement('input'); input.type = 'file'; input.accept = '.nii,.nii.gz'; input.onchange = async (e) => { const f = e.target.files[0]; if (!f) return; try { showToast('⏳ Загрузка NIfTI...', 'info', 2000); const vpIds = getViewportIds(); const vid = await loadNiftiFile(f, vpIds, 'mikhmelRenderingEngine', (p, msg) => showToast(`${p}% ${msg}`, 'info', 500)); showToast(`✅ ${vid}`, 'success', 2000); } catch(err) { showToast('❌ ' + err.message, 'error', 3000); } }; input.click(); } },
      { label: 'Экспорт NIfTI', action: () => { try { const data = getVolumeData?.(); if (!data) { showToast('Нет объём', 'warning'); return; } exportToNifti(data.scalarData, data.dims, data.spacing, `export_${Date.now()}.nii`); showToast('📤 NIfTI экспортирован', 'success', 2000); } catch(e) { showToast('❌ ' + e.message, 'error', 3000); } } },
    ],
  },

  // ══════════════════════════════════════════════════════════════
  //  25. ZIP АРХИВ (studyArchiveModule.js)
  // ══════════════════════════════════════════════════════════════

  // ══════════════════════════════════════════════════════════════
  //  26. VTK / STL ЭКСПОРТ (vtkModule.js)
  // ══════════════════════════════════════════════════════════════
  // [10.19.46] «VTK / STL Экспорт / Волюметрия» УДАЛЕНА (решение владельца,
  // п.7). Живой вход: меню Экспорт → 3D STL (btn-export-stl). «VTK доступен?»
  // — диагностика разработчика, её место в окне «Диагностика системы»
  // (10.19.44). Объёмы по порогу — родня клинических расчётов вкладки
  // «Клинические».

  // ══════════════════════════════════════════════════════════════
  //  27. GPU / СИСТЕМА (merged: gpuDetector + init.js)
  // ══════════════════════════════════════════════════════════════
  {
    id: 'system', icon: '', label: 'Система / GPU / Диагностика',
    buttons: [
      // [10.3.5] «GPU Status» убран — правый сайдбар, вкладка «GPU
      // Вычисления»: там уровень, рендерер, WebGPU/WebGL2, кэш.
      { label: 'Диагностика', action: async () => { try { const { getSystemDiagnostics } = await import('../core/init.js'); const d = getSystemDiagnostics(); showToast(`GPU: ${d.gpu.renderer}\nWebGL2: ${d.gpu.webgl2 ? '✅' : '❌'}\nVRAM: ${d.gpu.vramMB}MB\nКэш: ${d.cache.currentSizeMB}/${d.cache.maxSizeMB}MB (${d.cache.usagePercent}%)\nЭкран: ${d.browser.screenRes}\nRAM: ${d.browser.deviceMemoryGB}GB`, 'info', 8000); } catch (e) { showToast('❌ ' + e.message, 'error', 2000); } } },
      { label: 'FPS-плашка', action: () => toggleFPSOverlay() },
      { label: 'GPU Бенчмарк', action: async () => { showToast('⏳ Замер быстродействия...', 'info', 1000); const r = await runGPUBenchmark(); showToast(`Балл: ${r.score}\nСреднее: ${r.avgFPS} FPS\nМинимум: ${r.minFPS}\nМаксимум: ${r.maxFPS}`, r.score === 'Excellent' ? 'success' : 'info', 5000); } },
      { label: 'Проверка нагрева', action: () => { const t = detectThermalThrottling(); showToast(`Ограничение: ${t.throttling ? '⚠️ YES' : '✅ No'}\nПотери: ${t.dropPercent}%`, t.throttling ? 'warning' : 'success', 4000); } },
      { label: 'CS3D запущен?', action: () => { showToast(csIsInitialized() ? '✅ Cornerstone3D инициализирован' : '❌ Не инициализирован', csIsInitialized() ? 'success' : 'error', 2000); } },
      { label: 'Версии', action: async () => { try { const { getLibraryVersions } = await import('../core/init.js'); const v = getLibraryVersions(); showToast(`CS3D: ${v.cornerstone}\nИнструменты: ${v.tools}\nЗагрузчик: ${v.dicomLoader}`, 'info', 5000); } catch (e) { showToast('❌ ' + e.message, 'error'); } } },
    ],
  },

  // ══════════════════════════════════════════════════════════════
  //  28. ФАЙЛЫ (fileOps.js — упрощённый)
  // ══════════════════════════════════════════════════════════════
  {
    id: 'file-ops', icon: '', label: 'Файлы / Загрузка',
    buttons: [
      // [10.3.5] Убраны «Открыть файлы», «Открыть папку» и «Load from URL»
      // — меню Файл (btn-open-file / btn-open-folder / btn-open-url).
      // Отмена загрузки и история оставлены: в меню их нет.
      { label: 'Отменить загрузку', action: () => fopsCancelLoad() },
      { label: 'История загрузок', action: () => { const h = getLoadHistory(); showToast(h.length ? h.map(e => `${e.fileCount} files, ${e.totalSizeMB}MB, ${e.duration}ms`).join('\\n') : 'Нет загрузок', 'info', 5000); } },
    ],
  },

  // ══════════════════════════════════════════════════════════════
  //  29. PREMIUM UX (premiumFeatures.js)
  // ══════════════════════════════════════════════════════════════
  {
    id: 'premium-ux', icon: '', label: 'Голос и жесты',
    buttons: [
      // [10.3.5] «Night Mode» убран — верхняя панель справа, btn-night-mode.
      //
      // [11.1.2] СЮДА СВЕДЕНЫ ВСЕ ЧЕТЫРЕ функции. Раньше две жили здесь, а две
      // другие — «Голос» и «Жесты» — я перенёс из верхнего ряда в собственный
      // раздел той же вкладки, НЕ ЗАМЕТИВ этой группы: её нет в index.html,
      // она добавляется кодом при запуске, и проверка по разметке её не видит.
      // Вышло два блока об одном и том же с похожими названиями — врач нажал
      // бы «Голос» вместо «Голос: состояние» и получил не то.
      //
      // Имена теперь различают ЧЕТЫРЕ РАЗНЫЕ вещи, а не намекают на две:
      //   • «Голос: команды» — программа СЛУШАЕТ врача (распознавание речи);
      //   • «Голос: озвучка» — программа ГОВОРИТ врачу (статус вслух);
      //   • «Жесты: касания» — палец по экрану (щипок, свайп, двойное касание);
      //   • «Жесты: мышь» — Ctrl+Alt и рисование мышью (L, Z, ⭕).
      { label: 'Голос: команды', title: 'Программа слушает врача: «следующий срез», «окно лёгкие», «сброс». Нужен микрофон и разрешение браузера', action: async () => { const { переключитьГолос } = await import('./featureBindings.js'); переключитьГолос(); } },
      { label: 'Голос: озвучка', title: 'Программа говорит вслух о происходящем: загрузка, смена серии', action: async () => { const { toggleVoiceStatus } = await import('./premiumFeatures.js'); const on = toggleVoiceStatus(); showToast(on ? '🔊 Голосовой статус ВКЛ' : '🔇 Голосовой статус ВЫКЛ', 'info', 2000); } },
      { label: 'Жесты: касания', title: 'Сенсорный экран: щипок — масштаб, свайп — листание, двойное касание — сброс. На компьютере с мышью не сработают', action: async () => { const m = await import('./newFeaturesBindings.js'); await m.enableAllGestures(); } },
      { label: 'Жесты: мышь', title: 'Ctrl+Alt и рисование мышью: L — линейка, Z — масштаб, ⭕ — ROI', action: async () => { const { enableGestureShortcuts } = await import('./premiumFeatures.js'); enableGestureShortcuts(); showToast('✋ Жесты ВКЛ\nCtrl+Alt+Рисование: L=Линейка, Z=Масштаб, ⭕=ROI', 'success', 4000); } },
      // [10.19.47] Кнопка «Хронология: пример» снята: она рисовала
      // ВЫДУМАННЫЕ даты исследований — витрина в чистом виде.
    ],
  },

  // ══════════════════════════════════════════════════════════════
  //  30. КАЧЕСТВО КТ / ПРОТОКОЛЫ (studyQualityAnalyzer — клинические проверки)
  // ══════════════════════════════════════════════════════════════
  {
    id: 'quality-analyzer', icon: '', label: 'Качество КТ / Протоколы / Годность',
    buttons: [
      { label: 'Оценка качества', action: async () => { try { const studies = await getStudies(); if (!studies.length) { showToast('Нет исследований', 'info', 1500); return; } const q = await getStudyQuality(studies[0].id); const sc = StudyQualityAnalyzer.computeQualityScore(q.params); showToast(`🏆 Балл: ${sc.score}/100 (${sc.grade})\nСрез: ${sc.breakdown.slice}\nРеконструкция: ${sc.breakdown.recon}\nДоза: ${sc.breakdown.dose}\nПиксель: ${sc.breakdown.pixel}\nПротокол: ${sc.breakdown.protocol}`, 'info', 6000); } catch(e) { showToast('❌ ' + e.message, 'error', 3000); } } },
      { label: 'Соответствие (авто)', action: async () => { try { const studies = await getStudies(); if (!studies.length) { showToast('Нет исследований', 'info'); return; } const q = await getStudyQuality(studies[0].id); const c = StudyQualityAnalyzer.checkProtocolCompliance(q.params, 'auto'); showToast(`Протокол: ${c.protocolName}\nСоответствует: ${c.compliant ? '✅' : '❌'}\n${c.находки.map(f => `${f.severity === 'error' ? '❌' : '⚠️'} ${f.message}`).join('\n') || 'Всё ОК'}`, c.compliant ? 'success' : 'warning', 8000); } catch(e) { showToast('❌ ' + e.message, 'error', 3000); } } },
      { label: 'Соответствие: кардио', action: async () => { try { const studies = await getStudies(); if (!studies.length) { showToast('Нет', 'info'); return; } const q = await getStudyQuality(studies[0].id); const c = StudyQualityAnalyzer.checkProtocolCompliance(q.params, 'cardiac-cta'); showToast(`${c.protocolName}: ${c.compliant ? '✅' : '❌'}\n${c.находки.map(f => `${f.severity === 'error' ? '❌' : '⚠️'} ${f.message}`).join('\n') || 'Соответствует'}`, c.compliant ? 'success' : 'warning', 6000); } catch(e) { showToast('❌ ' + e.message, 'error'); } } },
      { label: 'Соответствие: кальций', action: async () => { try { const studies = await getStudies(); if (!studies.length) { showToast('Нет', 'info'); return; } const q = await getStudyQuality(studies[0].id); const c = StudyQualityAnalyzer.checkProtocolCompliance(q.params, 'calcium-score'); showToast(`${c.protocolName}: ${c.compliant ? '✅' : '❌'}\n${c.находки.map(f => `${f.severity === 'error' ? '❌' : '⚠️'} ${f.message}`).join('\n') || 'Соответствует'}`, c.compliant ? 'success' : 'warning', 6000); } catch(e) { showToast('❌ ' + e.message, 'error'); } } },
      { label: 'Соответствие: ТЭЛА', action: async () => { try { const studies = await getStudies(); if (!studies.length) { showToast('Нет', 'info'); return; } const q = await getStudyQuality(studies[0].id); const c = StudyQualityAnalyzer.checkProtocolCompliance(q.params, 'pe-cta'); showToast(`${c.protocolName}: ${c.compliant ? '✅' : '❌'}\n${c.находки.map(f => `${f.severity === 'error' ? '❌' : '⚠️'} ${f.message}`).join('\n') || 'Соответствует'}`, c.compliant ? 'success' : 'warning', 6000); } catch(e) { showToast('❌ ' + e.message, 'error'); } } },
      { label: 'Все протоколы', action: () => { const p = StudyQualityAnalyzer.listProtocols(); showToast(p.map(x => `${x.id}: ${x.name}`).join('\n'), 'info', 5000); } },
      { label: 'Годен: Коронаро?', action: async () => { try { const studies = await getStudies(); if (!studies.length) { showToast('Нет', 'info'); return; } const q = await getStudyQuality(studies[0].id); const r = StudyQualityAnalyzer.isAdequateFor(q.params, 'coronary-cta'); showToast(`КТ-коронарография: ${r.adequate ? '✅ Годен' : '❌ Не годен'}\nСрез: ${r.params.sliceThickness} мм\nКонтраст: ${r.params.hasContrast}\nЭКГ: ${r.params.hasЭКГGating}`, r.adequate ? 'success' : 'warning', 4000); } catch(e) { showToast('❌ ' + e.message, 'error'); } } },
      { label: 'Годен: ТЭЛА?', action: async () => { try { const studies = await getStudies(); if (!studies.length) return; const q = await getStudyQuality(studies[0].id); const r = StudyQualityAnalyzer.isAdequateFor(q.params, 'pe-cta'); showToast(`ТЭЛА: ${r.adequate ? '✅' : '❌'}`, r.adequate ? 'success' : 'warning', 2000); } catch(e) { showToast('❌ ' + e.message, 'error'); } } },
      { label: 'Годен: Аорта?', action: async () => { try { const studies = await getStudies(); if (!studies.length) return; const q = await getStudyQuality(studies[0].id); const r = StudyQualityAnalyzer.isAdequateFor(q.params, 'aorta'); showToast(`Аорта: ${r.adequate ? '✅' : '❌'}`, r.adequate ? 'success' : 'warning', 2000); } catch(e) { showToast('❌ ' + e.message, 'error'); } } },
      { label: 'Годен: ВРКТ?', action: async () => { try { const studies = await getStudies(); if (!studies.length) return; const q = await getStudyQuality(studies[0].id); const r = StudyQualityAnalyzer.isAdequateFor(q.params, 'hrct-lungs'); showToast(`ВРКТ: ${r.adequate ? '✅' : '❌'}`, r.adequate ? 'success' : 'warning', 2000); } catch(e) { showToast('❌ ' + e.message, 'error'); } } },
      { label: 'Батч анализ', action: async () => { showToast('⏳ Анализ всех...', 'info', 1500); const r = await batchAnalyzeQuality((c,t) => showToast(`${c}/${t}`, 'info', 500)); const ok = r.filter(x => !x.error).length; showToast(`✅ ${ok}/${r.length} проанализировано`, 'success', 3000); } },
      { label: 'Сравнить 1 и 2', action: async () => { try { const studies = await getStudies(); if (studies.length < 2) { showToast('Нужно ≥2 исследований', 'warning', 2000); return; } const c = await compareStudyQuality(studies[0].id, studies[1].id); showToast(`Исследование1 шум: ${c.study1.noiseLevel || '?'}\nИсследование2 шум: ${c.study2.noiseLevel || '?'}\nЛучше доза: ${c.comparison.betterDose === studies[0].id ? '#1' : '#2'}\nЛучше разрешение: ${c.comparison.betterResolution === studies[0].id ? '#1' : '#2'}`, 'info', 6000); } catch(e) { showToast('❌ ' + e.message, 'error', 3000); } } },
    ],
  },

  // ══════════════════════════════════════════════════════════════
  //  31. CINEMATIC СЛАЙДЕРЫ (studyToolsUI.js — без dev-кнопок)
  // ══════════════════════════════════════════════════════════════
  // [10.19.46] «Кинематографика / Пост-обработка» УДАЛЕНА (решение владельца,
  // п.3): единственная кнопка открывала панель кинематографики, которая живёт
  // сама по себе и доступна из мобильной навигации (mnav-cinematic).

  // ══════════════════════════════════════════════════════════════
  //  32. РАБОЧИЙ СТОЛ / ТЕМЫ (uiManager.js — пользовательские фичи)
  // ══════════════════════════════════════════════════════════════
  // [10.19.47] «Рабочие пространства / Темы / Шаг срезов» УДАЛЕНА (решение
  // владельца): пространства дублировали пресеты раскладки, темы — вопрос
  // настроек, а не работы врача над снимком.

  // ══════════════════════════════════════════════════════════════
  //  33. ИНСТРУМЕНТЫ / СЕГМЕНТАЦИЯ (toolManager.js — без dev-кнопок)
  // ══════════════════════════════════════════════════════════════
  // [10.19.47] «Переключение инструментов / Кисть» УДАЛЕНА (решение
  // владельца): пять «наборов» были макросами поверх тулбара, где все эти
  // инструменты и так под рукой.

  // ══════════════════════════════════════════════════════════════
  //  34. W/L ИСТОРИЯ + VRT (sidebarBindings.js — клинические фичи)
  // ══════════════════════════════════════════════════════════════
  // [10.19.47] «W/L История / Управление VRT» УДАЛЕНА (решение владельца):
  // история окна — забава, «Пользовательские VRT» — читалка списка,
  // «Синхр. ползунков кино» — служебное.

  // ══════════════════════════════════════════════════════════════
  //  35. VOLUME / DATASET INFO (proFeaturesBindings.js — клинические)
  // ══════════════════════════════════════════════════════════════
  // [10.19.47] «Том / Датасет / Память» УДАЛЕНА (решение владельца): чистая
  // диагностика разработчика — она живёт в окне «Диагностика системы»
  // (10.19.44), где её можно скопировать и отправить.
];


// ══════════════════════════════════════════════════════════════
//  RENDER & MOUNT
// ══════════════════════════════════════════════════════════════

/** Сколько кнопок в панели на самом деле — считаем, а не пишем числом. */
function _countButtons() {
  return SECTIONS.reduce((n, s) => n + (s.buttons?.length || 0), 0);
}

/**
 * Mount enterprise panel. Вызывать один раз после initUI().
 * Файл: src/main.js, после initUI():
 *
 *   import { разложитьРазделы } from './ui/enterprisePanel.js';
 *   разложитьРазделы();
 */
/**
 * [10.19.47] РОСПУСК ЗАВЕРШЁН: плавающей панели больше нет.
 *
 * Решение владельца — «остальное перераспредели в правом сайдбаре». Разделы
 * не переписаны заново: их ДЕЙСТВИЯ проверены годами и тестами, поэтому
 * переезжает не код кнопок, а МЕСТО, куда он раскладывается. Карта ниже —
 * единственный источник правды о том, где что живёт.
 *
 * Три судьбы: рабочие разделы — во вкладки правого сайдбара по темам;
 * диагностика и система — в окно «Диагностика системы» (10.19.44), где их
 * можно прочитать целиком и скопировать; клавиши и обзор интерфейса — в
 * окно справки.
 */
const КАРТА_ПЕРЕСЕЛЕНИЯ = Object.freeze({
  cine: { куда: 'tab-special', имя: 'Кино-прокрутка' },
  export: { куда: 'tab-report', имя: 'Экспорт и снимки' },
  clinical: { куда: 'tab-clinical', имя: 'Клинические расчёты' },
  'quality-analyzer': { куда: 'tab-clinical', имя: 'Качество КТ и годность' },
  '4d': { куда: 'tab-fusion', имя: '4D Кардио' },
  // [11.0.2] Раздел «Проекции…» переехал из tab-render в tab-special —
  // туда, где живут «Проекция слэба» и «Плоскости отсечения». Раньше одно
  // семейство было раскидано по двум вкладкам: ручной слэб и клип-ползунки в
  // «Спец. режимах», а их готовые сочетания («ТЭЛА MIP 10 мм», «Клип: кардио»)
  // — в «3D/Кинематике». Теперь и ручное, и готовое рядом.
  effects: { куда: 'tab-special', имя: 'Проекции, клиппинг, DE' },
  'layout-mgr': { куда: 'tab-special', имя: 'Раскладка и пресеты' },
  nifti: { куда: 'tab-special', имя: 'NIfTI' },
  'file-ops': { куда: 'tab-special', имя: 'Файлы и загрузка' },
  'premium-ux': { куда: 'tab-special', имя: 'Голос и жесты' },
  diag: { куда: 'modal-diagnostics', имя: 'Диагностика загрузки' },
  system: { куда: 'modal-diagnostics', имя: 'Система и GPU' },
  hotkeys: { куда: 'modal-help', имя: 'Горячие клавиши' },
  'inline-help': { куда: 'modal-help', имя: 'Обзор интерфейса' },
});

/** Группа раздела — свёрнутой, чтобы сайдбар не превратился в ленту. */
function _группаРаздела(section, имя) {
  const обёртка = document.createElement('div');
  обёртка.className = 'ep-group';
  обёртка.dataset.sectionId = section.id;

  const шапка = document.createElement('button');
  шапка.type = 'button';
  шапка.className = 'section-title ep-group-title';
  шапка.setAttribute('aria-expanded', 'false');
  шапка.innerHTML = `<span>${escHtml(имя)}</span><span class="ep-count">${section.buttons.length}</span>`;

  const тело = document.createElement('div');
  тело.className = 'ep-group-body';
  тело.hidden = true;

  шапка.addEventListener('click', () => {
    const открыт = !тело.hidden;
    тело.hidden = открыт;
    шапка.setAttribute('aria-expanded', String(!открыт));
  }, { signal: _ac.signal });

  for (const btn of section.buttons) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'ctrl-btn ep-btn';
    b.textContent = btn.label;
    b.dataset.sectionId = section.id;
    if (btn.active) b.dataset.hasActive = '1';
    b.addEventListener('click', async () => {
      try { await btn.action(); } catch (e) { showToast('❌ ' + (e.message || e), 'error', 2000); }
      _refreshActives(document);
    }, { signal: _ac.signal });
    if (btn.долгоеНажатие) {
      import('./expiryShift.js')
        .then((m) => m.привязатьДолгоеНажатие(b))
        .catch(() => { /* модуль недоступен — кнопка работает как обычно */ });
    }
    тело.appendChild(b);
  }

  обёртка.appendChild(шапка);
  обёртка.appendChild(тело);
  return обёртка;
}

/** Разложить разделы по домам. Идемпотентно: повтор не плодит копии. */
export function разложитьРазделы() {
  _injectStyles();
  document.querySelectorAll('.ep-group').forEach((г) => г.remove());
  const бездомные = [];

  for (const section of SECTIONS) {
    const назначение = КАРТА_ПЕРЕСЕЛЕНИЯ[section.id];
    if (!назначение) { бездомные.push(section.id); continue; }
    let дом = document.getElementById(назначение.куда);
    // В окнах кладём в тело карточки, а не в затемняющий оверлей — иначе
    // группа легла бы поверх фона и осталась недоступной.
    if (дом && дом.classList.contains('modal-overlay')) дом = дом.querySelector('.modal') || дом;
    if (!дом) { бездомные.push(section.id); continue; }
    дом.appendChild(_группаРаздела(section, назначение.имя));
  }

  if (бездомные.length) log.warn('[Разделы] без дома:', бездомные.join(', '));
  _refreshActives(document);

  /*
   * [11.0.2] ВЕЧНЫЙ ОПРОС КАЖДЫЕ 2 СЕКУНДЫ УБРАН. Было:
   *
   *   if (_epRefreshInterval) clearInterval(_epRefreshInterval);
   *   _epRefreshInterval = setInterval(() => _refreshActives(document), 2000);
   *
   * Комментарий у переменной обещал «ID интервала для очистки при unmount», но
   * unmountEnterprisePanel удалили вместе с панелью в 10.19.47 — снимать
   * таймер стало нечем, и он жил до закрытия вкладки.
   *
   * Работы в нём почти не было: обход делает 14 запросов к документу и
   * перебирает все 105 кнопок ради ТРЁХ, у которых вообще задана функция
   * `active()` (посчитано: `active:` встречается в файле трижды). Триста
   * лишних обходов в час — за подсветку трёх рамок.
   *
   * Теперь подсветка обновляется тогда, когда её могут увидеть: при щелчке по
   * любой кнопке раздела (это уже делал обработчик самой кнопки) и при
   * открытии вкладки или окна, где группы живут. Пока человек смотрит в
   * другое место, подсвечивать нечего.
   */
  document.querySelectorAll('.right-tab[data-tab], [data-mpanel]').forEach((вкл) => {
    вкл.addEventListener('click', () => setTimeout(() => _refreshActives(document), 60), { signal: _ac.signal });
  });
  for (const окно of ['modal-diagnostics', 'modal-help']) {
    const el = document.getElementById(окно);
    if (el) new MutationObserver(() => { if (el.classList.contains('open')) _refreshActives(document); })
      .observe(el, { attributes: true, attributeFilter: ['class'] });
  }
  log.debug('[Разделы] разложено групп:', SECTIONS.length, '· кнопок:', _countButtons());
}

// [10.19.47] mountEnterprisePanel УДАЛЕНА вместе с плавающей панелью:
// круглая кнопка-шестерня, выдвижное окно, затемнение, свайп вниз, таймер в
// углу. Разделы раскладываются по домам — разложитьРазделы() выше.




// [10.19.47] unmountEnterprisePanel удалена: снимать нечего.

// [10.19.47] toggleEnterprisePanel удалена: открывать нечего.

// ══════════════════════════════════════════════════════════════
//  INTERNALS
// ══════════════════════════════════════════════════════════════

function _refreshActives(корень = document) {
  // [10.19.47] Раньше подсветка искала кнопки СКВОЗНЫМ номером внутри одного
  // контейнера. После роспуска кнопки живут в разных вкладках и окнах —
  // сквозной номер подсвечивал бы чужую. Теперь адрес честный: раздел плюс
  // позиция внутри раздела.
  for (const section of SECTIONS) {
    const свои = корень.querySelectorAll(`.ep-btn[data-section-id="${section.id}"]`);
    section.buttons.forEach((btnDef, i) => {
      const el = свои[i];
      if (el && btnDef.active) {
        try { el.classList.toggle('ep-active', !!btnDef.active()); } catch (e) { /* состояние недоступно — обход не рушим */ }
      }
    });
  }
}

function _downloadUrl(dataUrl, filename) {
  const a = document.createElement('a'); a.href = dataUrl; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
}

function _downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  _downloadUrl(url, filename);
  URL.revokeObjectURL(url);
}

function _injectStyles() {
  // [10.19.47] Стили плавающей панели (шестерня, выдвижное окно, затемнение,
  // мобильная шторка, свайп-ручка) удалены вместе с ней. Осталось ровно то,
  // что нужно группам в сайдбаре и окнах: свёртка, счётчик, подсветка
  // «включено». Вид кнопок — сайдбарный (.ctrl-btn), чтобы переселенцы не
  // выглядели гостями.
  if (document.getElementById('ep-styles')) return;
  const style = document.createElement('style');
  style.id = 'ep-styles';
  style.textContent = `
    .ep-group { margin: 6px 0 2px; }
    .ep-group-title { display:flex; align-items:center; justify-content:space-between; gap:8px;
      width:100%; background:none; border:0; padding:6px 2px; cursor:pointer;
      color:var(--text-secondary,#94a3b8); font:inherit; text-align:left; }
    .ep-group-title:hover, .ep-group-title[aria-expanded="true"] { color:var(--text-primary,#e2e8f0); }
    .ep-count { font-size:10px; opacity:.6; }
    .ep-group-body { display:flex; flex-wrap:wrap; gap:4px; padding:2px 0 6px; }
    .ep-group-body .ep-btn { flex:1 1 46%; font-size:11px; padding:6px 8px; text-align:left; }
    .ep-btn.ep-active { outline:1px solid var(--accent,#4f8cff); color:var(--accent,#4f8cff); }
  `;
  document.head.appendChild(style);
}
